import { apiPost, apiPostNoContent } from './client';

export interface ShiftItemDTO {
  id: string;
  productId: string;
  planQty: number;
  factQty: number;
  payment: string;
}

export interface ShiftDTO {
  id: string;
  orderId: string;
  employeeId: string;
  shiftDate: string;
  description: string;
  status: string;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items?: ShiftItemDTO[];
}

export interface ShiftListItemDTO {
  id: string;
  orderId: string;
  employeeId: string;
  shiftDate: string;
  description: string;
  status: string;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Агрегат по позициям смены (ответ find). */
  totalPlanQty?: number;
  totalFactQty?: number;
  totalPayment?: string;
}

export const SHIFT_STATUSES = ['accepted', 'not_accepted', 'needs_revision'] as const;
export type ShiftStatus = (typeof SHIFT_STATUSES)[number];

export const SHIFT_STATUS_LABELS: Record<ShiftStatus, string> = {
  accepted: 'Принята',
  not_accepted: 'Не принята',
  needs_revision: 'На доработке',
};

export interface FindShiftsParams {
  orderId?: string;
  employeeId?: string;
  shiftDateFrom?: string;
  shiftDateTo?: string;
  status?: string;
  /** Серверный фильтр по наличию `closedAt`: `false` — только открытые, `true` — только закрытые; без поля — все. */
  hasClosedAt?: boolean;
  page?: number;
  itemsPerPage?: number;
  order?: Array<{ field: string; direction: string }>;
}

export interface FindShiftsResponse {
  items: ShiftListItemDTO[];
  count: number;
  /** Сумма к выплате по всей выборке (не только текущая страница), строка decimal. */
  totalPaymentSum?: string;
}

/** Поля фильтра для фасетов списка смен (без пагинации). */
export interface ShiftFacetFilterParams {
  orderId?: string;
  employeeId?: string;
  shiftDateFrom?: string;
  shiftDateTo?: string;
  status?: string;
  /** Должен совпадать с критерием `find` (вкладки сотрудника upcoming / closed / all). */
  hasClosedAt?: boolean;
}

export interface FindFilterShiftStatusesResponse {
  statuses: string[];
}

export interface FindFilterShiftOrdersResponse {
  orderIds: string[];
}

export interface FindByIDsShiftsResponse {
  items: Record<string, ShiftDTO>;
}

export interface CreateShiftItemParams {
  productId: string;
  planQty: number;
}

export interface CreateShiftParams {
  orderId: string;
  employeeId: string;
  shiftDate: string;
  description?: string;
  status: string;
  items: CreateShiftItemParams[];
}

export interface UpdateShiftItemParams {
  /** Id существующей позиции (find-by-ids); без id — новая строка */
  id?: string;
  productId: string;
  planQty: number;
  /** Опционально; без factQty для существующей позиции сервер сохраняет факт из БД */
  factQty?: number;
}

export interface UpdateShiftParams {
  id: string;
  orderId: string;
  employeeId: string;
  shiftDate: string;
  description?: string;
  status: string;
  items: UpdateShiftItemParams[];
}

export interface UpdateFactItemParams {
  id: string;
  factQty: number;
}

export interface UpdateFactParams {
  id: string;
  items: UpdateFactItemParams[];
}

/** DISTINCT статусов для выпадающего фильтра (критерий без поля status). */
export async function findFilterShiftStatuses(
  params: Pick<
    ShiftFacetFilterParams,
    'orderId' | 'employeeId' | 'shiftDateFrom' | 'shiftDateTo' | 'hasClosedAt'
  > = {},
): Promise<FindFilterShiftStatusesResponse> {
  const body: Record<string, unknown> = {
    orderId: params.orderId ?? '',
    employeeId: params.employeeId ?? '',
    shiftDateFrom: params.shiftDateFrom ?? '',
    shiftDateTo: params.shiftDateTo ?? '',
  };
  if (params.hasClosedAt !== undefined) {
    body.hasClosedAt = params.hasClosedAt;
  }
  return apiPost<FindFilterShiftStatusesResponse>('/api/shifts/find-filter-statuses', body);
}

/** DISTINCT заказов для выпадающего фильтра (критерий без поля orderId). */
export async function findFilterShiftOrders(
  params: Pick<
    ShiftFacetFilterParams,
    'employeeId' | 'shiftDateFrom' | 'shiftDateTo' | 'status' | 'hasClosedAt'
  > = {},
): Promise<FindFilterShiftOrdersResponse> {
  const body: Record<string, unknown> = {
    employeeId: params.employeeId ?? '',
    shiftDateFrom: params.shiftDateFrom ?? '',
    shiftDateTo: params.shiftDateTo ?? '',
    status: params.status ?? '',
  };
  if (params.hasClosedAt !== undefined) {
    body.hasClosedAt = params.hasClosedAt;
  }
  return apiPost<FindFilterShiftOrdersResponse>('/api/shifts/find-filter-orders', body);
}

export async function findShifts(params: FindShiftsParams = {}): Promise<FindShiftsResponse> {
  const body: Record<string, unknown> = {
    orderId: params.orderId ?? '',
    employeeId: params.employeeId ?? '',
    shiftDateFrom: params.shiftDateFrom ?? '',
    shiftDateTo: params.shiftDateTo ?? '',
    status: params.status ?? '',
    page: params.page ?? 1,
    itemsPerPage: params.itemsPerPage ?? 20,
    order: params.order ?? [{ field: 'shiftDate', direction: 'desc' }],
  };
  if (params.hasClosedAt !== undefined) {
    body.hasClosedAt = params.hasClosedAt;
  }
  return apiPost<FindShiftsResponse>('/api/shifts/find', body);
}

export async function findShiftsByIds(ids: string[]): Promise<FindByIDsShiftsResponse> {
  return apiPost<FindByIDsShiftsResponse>('/api/shifts/find-by-ids', { ids });
}

export async function createShift(params: CreateShiftParams): Promise<ShiftDTO> {
  return apiPost<ShiftDTO>('/api/shifts/create', params);
}

export async function updateShift(params: UpdateShiftParams): Promise<ShiftDTO> {
  return apiPost<ShiftDTO>('/api/shifts/update', params);
}

export async function updateFactShift(params: UpdateFactParams): Promise<ShiftDTO> {
  return apiPost<ShiftDTO>('/api/shifts/update-fact', params);
}

export async function deleteShifts(ids: string[]): Promise<void> {
  await apiPostNoContent('/api/shifts/delete', { ids });
}

export async function closeShift(id: string): Promise<void> {
  await apiPostNoContent('/api/shifts/close', { id });
}
